import { toast } from 'react-toastify';
import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * CreateProperty Component
 * 
 * Modal for creating new properties with modern UI/UX.
 * Features:
 * - Form validation
 * - Loading states
 * - Auto-populate comparative name
 * - Enhanced accessibility
 * - Duplicate / near-duplicate detection
 */
const CreateProperty = ({ onClose }) => {
    const [propertyName, setPropertyName] = useState('');
    const [comparitiveName, setComparitiveName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [similarProperties, setSimilarProperties] = useState([]);
    const { user, apiFetch } = useAuth();
    const debounceRef = useRef(null);

    const handleCancel = () => {
        if (onClose) onClose();
    };

    /** Check for similar property names (debounced, fires on blur or after typing stops) */
    const checkSimilar = useCallback(async (name) => {
        if (!name || name.trim().length < 3) { setSimilarProperties([]); return; }
        try {
            const res = await apiFetch(`/api/Property/check-similar?name=${encodeURIComponent(name.trim())}`);
            const data = await res.json();
            if (data.success) setSimilarProperties(data.data ?? []);
        } catch { /* silent — non-blocking */ }
    }, [apiFetch]);

    const handleNameChange = (e) => {
        const val = e.target.value;
        setPropertyName(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => checkSimilar(val), 500);
    };

    const handleNameBlur = () => checkSimilar(propertyName);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!propertyName.trim()) {
            toast.error('Property Name is required');
            return;
        }

        // Warn if there is an exact match still present
        const exactMatch = similarProperties.find(p => p.isExact);
        if (exactMatch) {
            toast.error(`A property named "${exactMatch.propertyName}" already exists.`);
            return;
        }

        setIsLoading(true);
        const userID = user.userID;

        const payload = {
            propertyName: propertyName.trim(),
            comparativeName: comparitiveName.trim() || propertyName.trim(),
            userID: userID
        };

        try {
            const response = await apiFetch('/api/Property/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                toast.success('Property created successfully');
                setPropertyName('');
                setComparitiveName('');
                setSimilarProperties([]);
                if (onClose) onClose();
            } else {
                toast.error(data.message || 'Failed to create property');
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error creating property:', err);
            toast.error('Network error');
        } finally {
            setIsLoading(false);
        }
    };

    const hasExact = similarProperties.some(p => p.isExact);
    const hasSimilar = similarProperties.length > 0;

    return (
        <div className="modal-overlay-modern" onClick={handleCancel}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                <h2>Create New Property</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group-modern">
                        <label htmlFor="propertyName">
                            Property Name<span className="required">*</span>
                        </label>
                        <input 
                            type="text" 
                            id="propertyName" 
                            name="propertyName" 
                            placeholder="Enter property name" 
                            value={propertyName} 
                            onChange={handleNameChange}
                            onBlur={handleNameBlur}
                            disabled={isLoading}
                            required
                            aria-required="true"
                            style={hasExact ? { borderColor: '#ef4444' } : hasSimilar ? { borderColor: '#f59e0b' } : undefined}
                        />
                        {hasSimilar && (
                            <div style={{
                                marginTop: 6,
                                padding: '8px 12px',
                                borderRadius: 7,
                                background: hasExact ? '#fef2f2' : '#fffbeb',
                                border: `1px solid ${hasExact ? '#fecaca' : '#fde68a'}`,
                                fontSize: '0.82rem',
                                color: hasExact ? '#991b1b' : '#92400e'
                            }}>
                                <FaExclamationTriangle style={{ marginRight: 6 }} />
                                {hasExact
                                    ? 'A property with this exact name already exists:'
                                    : 'Similar property names already exist — please confirm this is intentional:'}
                                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                    {similarProperties.map(p => (
                                        <li key={p.propertyID}>
                                            <strong>{p.propertyName}</strong>
                                            {p.isExact && <span style={{ marginLeft: 6, color: '#ef4444', fontWeight: 700 }}>(exact match)</span>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="form-group-modern">
                        <label htmlFor="comparitiveName">Comparative Name</label>
                        <input 
                            type="text" 
                            id="comparitiveName" 
                            name="comparitiveName" 
                            placeholder="Optional - defaults to property name if not provided" 
                            value={comparitiveName} 
                            onChange={(e) => setComparitiveName(e.target.value)}
                            disabled={isLoading}
                        />
                        <small className="field-helper-text">
                            If left blank, this will be set to the property name
                        </small>
                    </div>

                    <div className="modal-actions-modern">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={isLoading || hasExact}
                        >
                            {isLoading ? '✨ Creating...' : '✨ Create Property'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateProperty;